import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, XCircle, Clock, Phone, Mail, User, ShieldCheck } from "lucide-react";
import { fmtDateTime12 } from "@/lib/time";

export default function AdminMembershipRequests() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/membership-requests");
      setList(r.data);
    } catch {
      toast.error("Failed to load membership requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (req) => {
    if (!confirm(`Approve ${req.plan_name} for ${req.name} and credit ${inr(req.wallet_value)} to client wallet?`)) return;
    setBusyId(req.id);
    try {
      await api.post(`/admin/membership-requests/${req.id}/approve`);
      toast.success(`Request Approved! Credited ${inr(req.wallet_value)} to wallet.`);
      load();
    } catch {
      toast.error("Failed to approve request");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (req) => {
    if (!confirm(`Reject membership request for ${req.name}?`)) return;
    setBusyId(req.id);
    try {
      await api.post(`/admin/membership-requests/${req.id}/reject`);
      toast.success("Request Rejected");
      load();
    } catch {
      toast.error("Failed to reject request");
    } finally {
      setBusyId(null);
    }
  };

  const filteredList = list.filter((r) => {
    if (filter === "all") return true;
    return r.status === filter;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-amber-400 font-bold uppercase tracking-widest animate-pulse">Loading Requests...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-membership-requests" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-1 flex items-center gap-2">
            <CreditCard size={16} /> MASTER ONLY · CLIENT MEMBERSHIP APPROVALS
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Membership Requests
          </h1>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-2 bg-black/60 p-1.5 rounded-xl border border-white/15">
          {["all", "pending", "approved", "rejected"].map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-4 py-2 rounded-lg text-xs font-extrabold uppercase tracking-wider transition-all capitalize ${
                filter === tab
                  ? "bg-gradient-to-r from-amber-500 to-amber-300 text-black shadow-md"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Grid of Requests */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filteredList.map((req) => {
          const isPending = req.status === "pending";
          const isApproved = req.status === "approved";
          const isRejected = req.status === "rejected";

          return (
            <div
              key={req.id}
              className={`rounded-2xl border p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl flex flex-col justify-between space-y-4 hover:scale-[1.01] transition-all relative overflow-hidden ${
                isPending
                  ? "border-amber-400/80 shadow-amber-950/30"
                  : isApproved
                  ? "border-emerald-400/80 shadow-emerald-950/30"
                  : "border-red-500/80 shadow-red-950/30"
              }`}
            >
              {/* Header row */}
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <User className="text-amber-400" size={18} />
                    <h3 className="font-heading text-xl font-bold text-white">{req.name}</h3>
                  </div>
                  <span
                    className={`text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm flex items-center gap-1 ${
                      isPending
                        ? "bg-amber-950/80 border border-amber-400 text-amber-300"
                        : isApproved
                        ? "bg-emerald-950/80 border border-emerald-400 text-emerald-300"
                        : "bg-red-950/80 border border-red-500 text-red-300"
                    }`}
                  >
                    {isPending && <Clock size={11} />}
                    {isApproved && <CheckCircle2 size={11} />}
                    {isRejected && <XCircle size={11} />}
                    {req.status}
                  </span>
                </div>

                {/* Info List */}
                <div className="space-y-1.5 text-xs text-white/70">
                  <div className="flex items-center gap-2">
                    <Phone size={13} className="text-amber-400 shrink-0" />
                    <span>Phone: <strong className="text-white">{req.phone}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={13} className="text-amber-400 shrink-0" />
                    <span className="truncate">Email: <strong className="text-white">{req.email}</strong></span>
                  </div>
                </div>

                {/* Plan Details Box */}
                <div className="p-4 rounded-xl border border-amber-400/40 bg-amber-950/20 space-y-1.5">
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-400">REQUESTED MEMBERSHIP</div>
                  <div className="font-heading text-2xl font-bold text-white">{req.plan_name}</div>
                  <div className="flex items-center justify-between text-xs pt-1 font-semibold">
                    <span className="text-amber-300">Client Pays: <strong>{inr(req.price)}</strong></span>
                    <span className="text-emerald-400">Wallet Credit: <strong>{inr(req.wallet_value)}</strong></span>
                  </div>
                </div>

                {req.notes && (
                  <div className="text-xs text-white/60 italic bg-black/40 p-2.5 rounded-lg border border-white/10">
                    "{req.notes}"
                  </div>
                )}
              </div>

              {/* Action Footer */}
              <div className="pt-3 border-t border-white/10 space-y-3">
                <div className="text-[11px] text-white/40 font-semibold flex items-center gap-1.5">
                  <Clock size={12} className="text-amber-400" />
                  <span>Requested on {fmtDateTime12(req.created_at)}</span>
                </div>

                {isPending && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => approve(req)}
                      disabled={busyId === req.id}
                      className="flex-1 py-2.5 rounded-xl border border-emerald-400/70 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase tracking-wider transition-all shadow-md shadow-emerald-950/40 flex items-center justify-center gap-1.5"
                    >
                      <ShieldCheck size={15} /> Approve & Credit
                    </button>
                    <button
                      onClick={() => reject(req)}
                      disabled={busyId === req.id}
                      className="py-2.5 px-4 rounded-xl border border-red-500/50 bg-red-950/30 hover:bg-red-900/50 text-red-300 font-extrabold text-xs uppercase tracking-wider transition-all"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {filteredList.length === 0 && (
          <div className="col-span-full text-center py-16 text-white/50 bg-hpaper border border-white/10 rounded-2xl font-medium">
            No membership requests found in this category.
          </div>
        )}
      </motion.div>
    </div>
  );
}
